/**
 * CancelItemButton — per-line-item cancel + refund (India-only, ≤ ₹1500).
 *
 * Iter 151 (Feb 2026). Extracted from `WebsiteOrdersPanel.jsx` during the
 * Feb 2026 code-quality split.
 *
 * Mirrors backend eligibility (`check_cancel_eligibility` in server.py) so
 * the UI can show an inline verdict without a round-trip, but the server
 * is still authoritative. The button itself is rendered next to each
 * order item; the modal it opens captures reason + notes and fires
 * `POST /api/website-orders/{order_id}/items/{itemcode}/cancel`.
 */
import { useState, useMemo, useCallback } from "react";
import { Ban, X, XCircle, Check, AlertCircle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { websiteOrdersApi } from "../../lib/api";
import { INPUT_CLASS, formatMoney } from "./websiteOrdersUtils";

const _itemIsShipped = (it) => {
  const f = it?.shipped;
  if (f === true || f === 1) return true;
  if (typeof f === "string" && ["1", "y", "yes", "true", "shipped"].includes(f.trim().toLowerCase())) return true;
  if (typeof f === "number" && f) return true;
  if ((it?.ship_date || "").toString().trim()) return true;
  return false;
};

const _looksIndia = (o) => {
  const ac = String(o?.accessing_country || "").toUpperCase();
  const cc = String(o?.country || "").toUpperCase();
  const cur = String(o?.currency || o?.fx_type || "").toUpperCase();
  return ac === "IN" || cc === "IN" || cur === "INR";
};

const _itemPrice = (it) => {
  const raw = it?.price ?? it?.unit_price ?? it?.sellingprice ?? it?.order_finalprice ?? 0;
  const n = Number(raw);
  return isNaN(n) ? 0 : n;
};

const _hasReductions = (o) => {
  for (const [k, label] of [["giftvoucher", "gift voucher"], ["coupon", "coupon"], ["credit", "store credit"], ["custom_reduce", "custom discount"]]) {
    const raw = o?.[k];
    if (raw === null || raw === undefined || raw === "" || raw === 0 || raw === "0") continue;
    const n = Number(raw);
    if (!isNaN(n) && n > 0) return { has: true, which: label };
    if (isNaN(n) && String(raw).trim()) return { has: true, which: label };
  }
  return { has: false, which: "" };
};

// Returns { eligible, reason, hint } — UI gate only. Server re-checks.
const _clientEligibility = (order, item) => {
  if (!_looksIndia(order)) return { eligible: false, reason: "India-only", hint: "Instant cancellation is only supported for India orders today." };
  if (_itemIsShipped(item)) return { eligible: false, reason: "Item already shipped", hint: "Once delivered, customer may initiate a return within 7 days instead." };
  const price = _itemPrice(item);
  if (price > 1500) return { eligible: false, reason: "Item price > ₹1500", hint: "Current rollout cap is ₹1500/item. Transfer higher-value cancellations to Sales." };
  const pay = String(order?.payment_type || order?.paymenttype || "").toLowerCase();
  if (pay.includes("cod") || pay.includes("cash")) return { eligible: true, reason: "COD — eligible", hint: null };
  if (price > 5000) return { eligible: false, reason: "Online-paid > ₹5000", hint: "Upstream rejects online-paid items above ₹5,000." };
  const red = _hasReductions(order);
  if (red.has) return { eligible: false, reason: `Online-paid order has a ${red.which} applied`, hint: "Upstream rejects instant refunds when a voucher/coupon/credit/custom discount has been used." };
  return { eligible: true, reason: "Razorpay under ₹5k with no reductions — eligible", hint: null };
};

export const CancelItemButton = ({ order, item, onCancelled }) => {
  const elig = useMemo(() => _clientEligibility(order, item), [order, item]);
  const alreadyCancelled = String(item?.cancel_status || item?.status || "").toLowerCase().includes("cancel");
  const shipped = _itemIsShipped(item);

  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [action, setAction] = useState("cancel"); // "cancel" | "return"
  const [reasonCode, setReasonCode] = useState("changed_mind");
  const [reasonFree, setReasonFree] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { refund_id, suggested_customer_message, audit_id }

  const loadReasons = useCallback(async () => {
    if (reasons.length) return;
    setLoadingReasons(true);
    try {
      const res = await websiteOrdersApi.listCancelReasons();
      const list = res.data?.reasons || [];
      setReasons(list);
      if (list.length && !list.find((r) => r.code === reasonCode)) {
        setReasonCode(list[0].code);
      }
    } catch (e) {
      toast.error("Couldn't load cancellation reasons");
    } finally {
      setLoadingReasons(false);
    }
  }, [reasons.length, reasonCode]);

  const handleOpen = () => {
    setResult(null);
    setReasonFree("");
    setAction("cancel");
    setOpen(true);
    loadReasons();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await websiteOrdersApi.cancelItem(order.id, item.itemcode, {
        reason_code: reasonCode,
        reason_free_text: reasonFree || null,
        action,
        lead_id: order.lead_id || null,
      });
      const data = res.data || {};
      setResult({
        refund_id: data.refund_id,
        suggested_customer_message: data.suggested_customer_message,
        audit_id: data.audit_id,
      });
      toast.success(action === "return" ? "Return recorded" : "Item cancelled & refund initiated");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Cancellation failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (result) onCancelled?.();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select & copy manually");
    }
  };

  if (alreadyCancelled) {
    return (
      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200" data-testid={`cancel-item-badge-${order.id}-${item.itemcode}`}>
        Cancelled
      </span>
    );
  }
  if (shipped) {
    return null; // Shipped items can't be instant-cancelled — hide button entirely.
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        disabled={!elig.eligible}
        title={elig.eligible ? "Cancel this item & initiate refund" : elig.hint || elig.reason}
        className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border transition-colors ${
          elig.eligible
            ? "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"
            : "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
        }`}
        data-testid={`cancel-item-btn-${order.id}-${item.itemcode}`}
      >
        <Ban size={9} />
        {elig.eligible ? "Cancel" : "N/A"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={handleClose}
          data-testid="cancel-item-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="cancel-item-modal"
          >
            {!result ? (
              <>
                <div className="flex items-start justify-between p-4 border-b border-slate-100">
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-1.5">
                      <XCircle size={14} className="text-rose-600" />
                      Cancel item & refund
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Order #{order.id} · {item.order_title || item.itemcode}
                    </p>
                  </div>
                  <button onClick={handleClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="cancel-item-modal-close">
                    <X size={16} />
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900">
                    <p className="font-bold mb-0.5 flex items-center gap-1"><AlertCircle size={11} /> Heads up</p>
                    <p className="leading-snug">
                      This triggers an upstream refund{order?.payment_type?.toLowerCase?.().includes("razorpay") ? " via Razorpay" : ""}. An ops-team email is auto-sent. This can't be undone from the CRM.
                    </p>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Action</label>
                    <div className="flex gap-1.5" data-testid="cancel-item-action-toggle">
                      {["cancel", "return"].map((a) => (
                        <button
                          key={a}
                          type="button"
                          onClick={() => setAction(a)}
                          className={`flex-1 text-[11px] font-semibold px-2 py-1.5 rounded-lg border transition-colors ${
                            action === a
                              ? "bg-rose-600 text-white border-rose-600"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}
                          data-testid={`cancel-item-action-${a}`}
                        >
                          {a === "cancel" ? "Cancel & refund" : "Record return"}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Reason *</label>
                    <select
                      value={reasonCode}
                      onChange={(e) => setReasonCode(e.target.value)}
                      disabled={loadingReasons}
                      className={INPUT_CLASS}
                      data-testid="cancel-item-reason-select"
                    >
                      {loadingReasons && <option>Loading…</option>}
                      {!loadingReasons && reasons.map((r) => (
                        <option key={r.code} value={r.code}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Notes (optional)</label>
                    <textarea
                      value={reasonFree}
                      onChange={(e) => setReasonFree(e.target.value)}
                      rows={3}
                      placeholder="Anything ops should know — call notes, WhatsApp timestamp, etc."
                      className={INPUT_CLASS + " resize-none"}
                      data-testid="cancel-item-reason-notes"
                    />
                  </div>

                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[10.5px] text-slate-600 space-y-0.5">
                    <div className="flex justify-between"><span>Refund amount</span><span className="font-bold text-slate-800">{formatMoney(_itemPrice(item), order.fx_type || "INR", order.fx_notation)}</span></div>
                    <div className="flex justify-between"><span>Payment mode</span><span className="font-semibold">{order.payment_type || "—"}</span></div>
                    <div className="flex justify-between"><span>Eligibility</span><span className={elig.eligible ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>{elig.eligible ? "OK" : elig.reason}</span></div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitting}
                    className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700"
                    data-testid="cancel-item-modal-dismiss"
                  >
                    Never mind
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !elig.eligible}
                    className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    data-testid="cancel-item-modal-confirm"
                  >
                    {submitting ? <><Loader2 size={12} className="animate-spin" />Processing…</> : <><Ban size={11} />{action === "return" ? "Record return" : "Cancel & refund"}</>}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start justify-between p-4 border-b border-slate-100">
                  <div>
                    <h3 className="text-sm font-extrabold text-emerald-700 flex items-center gap-1.5">
                      <Check size={14} />
                      {action === "return" ? "Return recorded" : "Cancelled & refund initiated"}
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">Audit ID: <code className="font-mono">{result.audit_id || "—"}</code></p>
                  </div>
                  <button onClick={handleClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="cancel-item-result-close">
                    <X size={16} />
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  {result.refund_id && (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5 text-[11px] text-emerald-900">
                      <p className="font-bold mb-0.5">Refund ID</p>
                      <p className="font-mono text-[10.5px]">{result.refund_id}</p>
                      <p className="leading-snug mt-1 text-emerald-800/80">Customer's bank typically credits within 7 business days.</p>
                    </div>
                  )}

                  {result.suggested_customer_message && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Suggested customer message</label>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(result.suggested_customer_message)}
                          className="text-[10px] font-semibold text-violet-700 hover:text-violet-900 flex items-center gap-1"
                          data-testid="cancel-item-copy-message"
                        >
                          <Sparkles size={10} />Copy
                        </button>
                      </div>
                      <pre className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[10.5px] text-slate-700 whitespace-pre-wrap font-sans leading-snug" data-testid="cancel-item-suggested-message">
                        {result.suggested_customer_message}
                      </pre>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-900"
                    data-testid="cancel-item-result-done"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
